import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { JourneyPlannerFacade } from './state+/journey-planner.facade';
import Env from '@ridango/core/env';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { FavoriteStop, FavoriteStopResponse } from './journey-planner.model';
import { AuthService } from '@ridango/core';
import { JsonUtil } from '@ridango/core/lib/utils/json-util';

@Injectable({
    providedIn: 'root'
})
export class JourneyPlannerService {
    public favoriteStopsLocalStorageKey = `${this.env.APP_NAME}-journey-planner-favorite-stops`;

    constructor(
        @Inject('ENV') public env: Env,
        private authService: AuthService,
        private http: HttpClient,
        private journeyPlannerFacade: JourneyPlannerFacade
    ) {}

    syncLocalFavoriteWithBE(): void {
        if (this.authService.getLoginStatus()) {
            const favorites = this.getFavoriteStopsFromLocalStorage();
            const isExistUnsavedFavoriteStop = favorites.some((stop) => typeof stop.id === 'string');

            if (isExistUnsavedFavoriteStop && favorites.length) {
                const unsavedFavorites = favorites.filter((stop) => typeof stop.id === 'string');
                unsavedFavorites.forEach((stop) => this.journeyPlannerFacade.addFavoriteStop(stop));
            }
        }
    }

    checkFavoriteStops(current: FavoriteStop, next: FavoriteStop): boolean {
        if (!current || !next) {
            return false;
        }

        return (
            current.fromPlace.lat === next.fromPlace.lat &&
            current.fromPlace.lng === next.fromPlace.lng &&
            current.toPlace.lat === next.toPlace.lat &&
            current.toPlace.lng === next.toPlace.lng
        );
    }

    filterCheckFavoriteStops(current: FavoriteStop, next: FavoriteStop): boolean {
        if (!current || !next) {
            return false;
        }

        return current.id !== next.id;
    }

    getFavoriteStopsFromLocalStorage(): FavoriteStop[] {
        return JsonUtil.parse<FavoriteStop[]>(
            localStorage.getItem(this.favoriteStopsLocalStorageKey),
            Array.isArray,
            []
        );
    }

    getFavoriteStops(): Observable<FavoriteStop[]> {
        this.syncLocalFavoriteWithBE();

        if (this.authService.getLoginStatus()) {
            const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/favorite/search`;
            return this.http.get(url).pipe(
                map((favorites: FavoriteStopResponse[]) =>
                    favorites.map((favorite) => this.convertToFavorite(favorite))
                ),
                tap((favorites) => {
                    localStorage.setItem(this.favoriteStopsLocalStorageKey, JSON.stringify(favorites));
                })
            );
        } else {
            return of(this.getFavoriteStopsFromLocalStorage());
        }
    }

    addFavoriteStop(payload: FavoriteStop): Observable<FavoriteStop> {
        if (this.authService.getLoginStatus()) {
            try {
                const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/favorite/search`;
                const requestBody = this.convertToFavoriteRequestFormat(payload);

                return this.http.post<FavoriteStopResponse>(url, requestBody).pipe(
                    map((favorite) => this.convertToFavorite(favorite)),
                    tap((favorite: FavoriteStop) => this.addFavoriteToLocalStorage(favorite))
                );
            } catch (err) {
                console.error('JourneyPlanner.addFavorites', err);
            }
        } else {
            return of(this.addFavoriteToLocalStorage(payload));
        }
    }

    deleteFavoriteStops(stops: FavoriteStop[]): Observable<FavoriteStop[]> {
        if (this.authService.getLoginStatus()) {
            stops.forEach((stop) => this.deleteFavoriteStop(stop));
        } else {
            // Delete only inside local storage
            stops.forEach((stop) => this.removeFavoriteInLocalStorage(stop));
        }
        return of(this.getFavoriteStopsFromLocalStorage());
    }

    deleteFavoriteStop(stop: FavoriteStop): Observable<FavoriteStop[]> {
        if (this.authService.getLoginStatus()) {
            try {
                // Delete favorite stop from local storage
                const filteredFavoriteStops = this.removeFavoriteInLocalStorage(stop);

                const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/favorite/search/${stop.id}`;
                this.http.delete(url).subscribe();
                return of(filteredFavoriteStops);
            } catch (err) {
                console.error('JourneyPlanner.deleteFromFavorite', err);
            }
        } else {
            const filteredFavoriteStops = this.removeFavoriteInLocalStorage(stop);
            return of(filteredFavoriteStops);
        }
    }

    private uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c == 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    private convertToFavorite(favorite: FavoriteStopResponse): FavoriteStop {
        return {
            id: favorite.id,
            fromPlaceName: favorite.from.name,
            fromPlace: { lat: favorite.from.lat, lng: favorite.from.lon, stopId: favorite.from.stopId },
            toPlaceName: favorite.to.name,
            toPlace: { lat: favorite.to.lat, lng: favorite.to.lon, stopId: favorite.to.stopId }
        };
    }

    private formatPlaceName(favorite) {
        const formatPlaceName = (placeName: string, municipality: string | undefined) =>
            municipality ? `${placeName} (${municipality})` : placeName;

        const fromPlaceMunicipalityName = this.env.JOURNEY_PLANNER?.SHOW_MUNICIPALITY_IN_STOP_NAME
            ? formatPlaceName(favorite.fromPlaceName, favorite.municipalityName?.origin)
            : favorite.fromPlaceName;

        const toPlaceMunicipalityName = this.env.JOURNEY_PLANNER?.SHOW_MUNICIPALITY_IN_STOP_NAME
            ? formatPlaceName(favorite.toPlaceName, favorite.municipalityName?.destination)
            : favorite.toPlaceName;

        return { fromPlaceMunicipalityName, toPlaceMunicipalityName };
    }

    private convertToFavoriteRequestFormat(favorite: FavoriteStop): FavoriteStopResponse {
        const { fromPlaceMunicipalityName, toPlaceMunicipalityName } = this.formatPlaceName(favorite);

        return {
            id: favorite.id as number,
            from: {
                name: fromPlaceMunicipalityName || favorite.fromPlaceName,
                lat: favorite.fromPlace.lat,
                lon: favorite.fromPlace.lng,
                stopId: favorite.fromPlace.stopId
            },
            to: {
                name: toPlaceMunicipalityName || favorite.toPlaceName,
                lat: favorite.toPlace.lat,
                lon: favorite.toPlace.lng,
                stopId: favorite.toPlace.stopId
            }
        };
    }

    private removeFavoriteInLocalStorage(stop: FavoriteStop) {
        const prevStateFavoriteStops = this.getFavoriteStopsFromLocalStorage();
        const filteredFavoriteStops = prevStateFavoriteStops.filter((prevStateStop: FavoriteStop) =>
            this.filterCheckFavoriteStops(prevStateStop, stop)
        );
        localStorage.setItem(this.favoriteStopsLocalStorageKey, JSON.stringify(filteredFavoriteStops));
        return filteredFavoriteStops;
    }

    private addFavoriteToLocalStorage(favorite: FavoriteStop): FavoriteStop {
        const prevValue = this.getFavoriteStopsFromLocalStorage();
        const { fromPlaceMunicipalityName, toPlaceMunicipalityName } = this.formatPlaceName(favorite);
        const tempFavorite = {
            ...favorite,
            id: favorite?.id ? favorite.id : this.uuidv4(),
            fromPlaceName: fromPlaceMunicipalityName,
            toPlaceName: toPlaceMunicipalityName
        };

        localStorage.setItem(this.favoriteStopsLocalStorageKey, JSON.stringify([tempFavorite, ...prevValue]));
        return tempFavorite;
    }
}
